import { useMemo, useState } from "react";
import { Config } from "../config/Config";
import { IArticle } from "../models/IArticle";
import { ITestimonial, IPricingTier, IPartner, ITeamMember, ICaseStudy, ISiteSetting } from "../models/ICmsModels";

export interface ICmsApi {
	isLoading: boolean;
	getArticles: (max?: number) => Promise<IArticle[]>;
	getTestimonials: () => Promise<ITestimonial[]>;
	getPricingTiers: () => Promise<IPricingTier[]>;
	getPartners: () => Promise<IPartner[]>;
	getTeamMembers: () => Promise<ITeamMember[]>;
	getCaseStudies: () => Promise<ICaseStudy[]>;
	getSiteSettings: () => Promise<ISiteSetting[]>;
	getSiteSetting: (key: string) => Promise<ISiteSetting | null>;
}

export const useCmsApi = (): ICmsApi => {
	const baseUrl = Config.baseUrl;
	const [loadingCount, setLoadingCount] = useState(0);
	const isLoading = loadingCount > 0;

	// Memoize API methods so references are stable across renders.
	// This allows consumers to include them in useEffect deps without infinite loops.
	const api = useMemo(() => {
		const fetchJson = async <T,>(url: string, fallback: T): Promise<T> => {
			setLoadingCount(c => c + 1);
			try {
				const headers = { accept: "application/json" };
				const response = await fetch(url, { method: "GET", headers });
				if (!response.ok) { throw new Error(`HTTP ${response.status}`); }
				return await response.json() as T;
			} catch (error) {
				console.error('CMS API error:', error);
				return fallback;
			} finally {
				setLoadingCount(c => c - 1);
			}
		};

		// Note: Separate from useApi.getArticles() to avoid MSAL dependency in public CMS hook
		const getArticles = async (max: number = 20): Promise<IArticle[]> => {
			return fetchJson<IArticle[]>(`${baseUrl}api/v1/article?max=${max}`, []);
		};

		const getTestimonials = async (): Promise<ITestimonial[]> => {
			return fetchJson<ITestimonial[]>(`${baseUrl}api/v1/testimonial`, []);
		};

		const getPricingTiers = async (): Promise<IPricingTier[]> => {
			return fetchJson<IPricingTier[]>(`${baseUrl}api/v1/pricingtier`, []);
		};

		const getPartners = async (): Promise<IPartner[]> => {
			return fetchJson<IPartner[]>(`${baseUrl}api/v1/partner`, []);
		};

		const getTeamMembers = async (): Promise<ITeamMember[]> => {
			return fetchJson<ITeamMember[]>(`${baseUrl}api/v1/teammember`, []);
		};

		const getCaseStudies = async (): Promise<ICaseStudy[]> => {
			return fetchJson<ICaseStudy[]>(`${baseUrl}api/v1/casestudy`, []);
		};

		const getSiteSettings = async (): Promise<ISiteSetting[]> => {
			return fetchJson<ISiteSetting[]>(`${baseUrl}api/v1/sitesettings`, []);
		};

		const getSiteSetting = async (key: string): Promise<ISiteSetting | null> => {
			return fetchJson<ISiteSetting | null>(`${baseUrl}api/v1/sitesettings/${key}`, null);
		};

		return {
			getArticles,
			getTestimonials,
			getPricingTiers,
			getPartners,
			getTeamMembers,
			getCaseStudies,
			getSiteSettings,
			getSiteSetting,
		};
	}, [baseUrl]);

	return { isLoading, ...api };
};
